import { ChevronDown, MessageCircle } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { useImagePreview } from '@/hooks/use-image-preview'

import { ImagePreviewModal } from '@/components/common/image-preview-modal'
import { Button } from '@/components/ui/button'

import {
  getMessageSeriesGroupClassName,
  getMessageSeriesMeta,
  groupMessageIndices,
} from '@/utils/chat-message-series'
import { getMessageViewers as getMessageViewersFromParticipants, MessageViewer } from '@/utils/chat-message-viewers'
import { formatTimestampDivider, shouldShowTimestampDivider } from '@/utils/chat-timestamp'

import { MessageBoxWithActions } from './message-box-with-actions'
import { MessageUserAvatar } from './message-user-avatar'
import { MessageViewersDialog } from './message-viewers'
import { TimestampDivider } from './timestamp-divider'
import { useScrollToBottomVisibility } from '../hooks/use-scroll-to-bottom-visibility'

import { ConversationParticipant, Message, MessageEmojiData, MessageReactionType, MessageType } from '@/types/chat.type'

export type FileMessage = {
  type: MessageType
  fileUrl: string
  fileName?: string
  fileSize?: number
  content: string
  isImage: boolean
  isVideo?: boolean
}

interface ChatMessagesProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messages: any[]
  selectedConversationId: number
  isLoading: boolean
  isLoadingPrevious: boolean
  isLoadingMore: boolean
  hasPreviousMessages: boolean
  hasMoreMessages: boolean
  contextMessagesEndIndex: number | null
  searchQuery: string
  editingMessageId: number | null
  editMessageContent: string
  onLoadPreviousMessages: () => void
  onLoadMoreMessages: () => void
  onStartEditMessage: (messageId: number, currentContent: string) => void
  onEditMessage: () => void
  onCancelEditMessage: () => void
  onEditMessageContentChange: (content: string) => void
  onShowDeleteConfirm: (messageId: number) => void
  onStartReply: (message: Message) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMessageSenderClick: (sender: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderFileMessage: (message: any) => FileMessage | null
  formatTimestamp: (timestamp: string) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authUser: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scrollManager: any
  onScrollToMessage?: (messageId: number) => void
  participants?: ConversationParticipant[]
  onMessageReact?: (messageId: number, reactionType: MessageReactionType, emojiData?: MessageEmojiData) => void
  highlightedMessageId?: number | null
  onPinMessage?: (messageId: number) => void
  onUnpinMessage?: (messageId: number) => void
  isTyping?: boolean
  typingUserName?: string | null
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  selectedConversationId,
  isLoading,
  isLoadingPrevious,
  isLoadingMore,
  hasPreviousMessages,
  hasMoreMessages,
  contextMessagesEndIndex,
  searchQuery,
  editingMessageId,
  editMessageContent,
  onLoadPreviousMessages,
  onLoadMoreMessages,
  onStartEditMessage,
  onEditMessage,
  onCancelEditMessage,
  onEditMessageContentChange,
  onShowDeleteConfirm,
  onStartReply,
  onMessageSenderClick,
  renderFileMessage,
  formatTimestamp,
  authUser,
  scrollManager,
  onScrollToMessage,
  participants = [],
  onMessageReact,
  highlightedMessageId,
  onPinMessage,
  onUnpinMessage,
  isTyping = false,
  typingUserName = null,
}) => {
  // Handle user profile click
  const handleUserClick = (viewer: {
    id: number
    user: {
      firstName: string
      lastName: string
      avatar?: string
      profileId?: string
      id?: number
    }
    readAt: string | null
  }) => {
    // Close the viewers dialog
    setViewingMessage(null)
    // Use the same handler as message sender click to close chat popup
    // Create a user object with required id field
    const userForClick = {
      id: viewer.user.id || viewer.id,
      firstName: viewer.user.firstName,
      lastName: viewer.user.lastName,
      avatar: viewer.user.avatar,
      profileId: viewer.user.profileId,
    }
    onMessageSenderClick(userForClick)
  }

  // Use the scroll refs from the scrollManager instead of creating new ones
  const { messagesEndRef, messagesContainerRef } = scrollManager

  // Track scroll position to show/hide scroll-to-bottom button
  const { isScrolledUp } = useScrollToBottomVisibility({
    messagesContainerRef,
    messagesEndRef,
    threshold: 50,
    isLoadingPrevious,
    contentLength: messages.length,
  })

  // Refs for auto-loading functionality
  const loadPreviousObserverRef = React.useRef<IntersectionObserver | null>(null)
  const loadMoreObserverRef = React.useRef<IntersectionObserver | null>(null)
  const hasAutoLoadedRef = React.useRef(false)
  const isAutoLoadEnabledRef = React.useRef(false)

  // Delay auto-load activation when conversation changes to allow initial scroll to complete
  React.useEffect(() => {
    // Disable auto-load initially
    isAutoLoadEnabledRef.current = false

    // Enable auto-load after a delay to allow initial scroll to bottom
    const timer = setTimeout(() => {
      isAutoLoadEnabledRef.current = true
    }, 1500) // 1.5 seconds delay after conversation loads

    return () => clearTimeout(timer)
  }, [selectedConversationId])

  // Callback ref attaches an IntersectionObserver to the "Load Previous" button
  // as it mounts/unmounts, so auto-load also triggers on manual scroll-up.
  const loadPreviousButtonCallbackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (loadPreviousObserverRef.current) {
        loadPreviousObserverRef.current.disconnect()
        loadPreviousObserverRef.current = null
      }

      if (!node || isLoadingPrevious || !hasPreviousMessages) return

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (
              entry.isIntersecting &&
              !isLoadingPrevious &&
              hasPreviousMessages &&
              !hasAutoLoadedRef.current &&
              isAutoLoadEnabledRef.current
            ) {
              hasAutoLoadedRef.current = true
              onLoadPreviousMessages()
              setTimeout(() => {
                hasAutoLoadedRef.current = false
              }, 1000)
            }
          })
        },
        {
          root: messagesContainerRef.current,
          rootMargin: '100px 0px 0px 0px',
          threshold: 0,
        }
      )

      observer.observe(node)
      loadPreviousObserverRef.current = observer
    },
    [isLoadingPrevious, hasPreviousMessages, onLoadPreviousMessages, messagesContainerRef]
  )

  // Scroll listener: fire auto-load on every upward scroll tick near the top,
  // so it triggers even if the button was already in view before the grace period.
  const lastScrollTopRef = React.useRef(0)
  React.useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const current = container.scrollTop
      const scrolledUp = current < lastScrollTopRef.current
      lastScrollTopRef.current = current

      if (
        scrolledUp &&
        current <= 100 &&
        hasPreviousMessages &&
        !isLoadingPrevious &&
        !hasAutoLoadedRef.current
      ) {
        hasAutoLoadedRef.current = true
        onLoadPreviousMessages()
        setTimeout(() => {
          hasAutoLoadedRef.current = false
        }, 1000)
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [hasPreviousMessages, isLoadingPrevious, onLoadPreviousMessages, messagesContainerRef])


  // Create callback ref for load more button to handle dynamic element
  const loadMoreButtonCallbackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      // Disconnect previous observer
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect()
        loadMoreObserverRef.current = null
      }

      // Set up new observer if node exists
      if (node && !isLoadingMore && hasMoreMessages && isAutoLoadEnabledRef.current) {
        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (
                entry.isIntersecting &&
                !isLoadingMore &&
                hasMoreMessages &&
                !hasAutoLoadedRef.current &&
                isAutoLoadEnabledRef.current
              ) {
                hasAutoLoadedRef.current = true
                onLoadMoreMessages()
                // Reset after a short delay to allow subsequent loads
                setTimeout(() => {
                  hasAutoLoadedRef.current = false
                }, 1000)
              }
            })
          },
          {
            root: messagesContainerRef.current,
            threshold: 0.1,
          }
        )

        observer.observe(node)
        loadMoreObserverRef.current = observer
      }
    },
    [isLoadingMore, hasMoreMessages, onLoadMoreMessages, messagesContainerRef]
  )

  // Cleanup observer on unmount
  React.useEffect(() => {
    return () => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect()
      }
      if (loadPreviousObserverRef.current) {
        loadPreviousObserverRef.current.disconnect()
      }
    }
  }, [])

  const getMessageViewers = React.useCallback(
    (message: Message) => getMessageViewersFromParticipants(message, participants),
    [participants]
  )

  const [viewingMessage, setViewingMessage] = React.useState<{ viewers: MessageViewer[] } | null>(null)

  // State to track which message options are open
  const [openMessageId, setOpenMessageId] = React.useState<number | null>(null)

  // Click outside detection
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.message-options-container')) {
        setOpenMessageId(null)
      }
    }

    if (openMessageId) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMessageId])

  return (
    <>
      {/* Messages */}
      <div className="relative flex-1 min-h-0">
      <div
        className="h-full overflow-y-auto overflow-x-hidden p-3 sm:p-4 space-y-3 sm:space-y-4"
        ref={messagesContainerRef}
      >
        {/* Loading More Indicator */}
        {isLoadingPrevious && (
          <div className="flex justify-center py-2">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
              <div className="w-3 h-3 sm:w-4 sm:h-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Loading previous messages...
            </div>
          </div>
        )}

        {/* Load More Button */}
        {selectedConversationId && hasPreviousMessages && !isLoadingPrevious && messages.length > 0 && (
          <div ref={loadPreviousButtonCallbackRef} className="flex justify-center">
            <Button
              onClick={onLoadPreviousMessages}
              disabled={isLoadingPrevious}
              variant="outline"
              size="sm"
              className="text-xs"
            >
              Load Previous Messages
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3 sm:space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 sm:h-16 bg-gray-200 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6 sm:py-8 text-gray-500">
            <MessageCircle className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-gray-400" />
            <p className="text-base sm:text-lg font-medium">
              {searchQuery.trim() ? 'No messages found' : 'No messages yet'}
            </p>
            <p className="text-sm">{searchQuery.trim() ? 'Try a different search term' : 'Start the conversation!'}</p>
          </div>
        ) : (
          <>
            {/* Search Results Indicator */}
            {searchQuery.trim() && (
              <div className="text-center py-2 mb-2">
                <p className="text-xs text-gray-500">
                  Found {messages.length} message{messages.length !== 1 ? 's' : ''} matching "{searchQuery}" in this
                  conversation
                </p>
              </div>
            )}
            {/* Messages */}
            {groupMessageIndices(messages).map(groupIndices => {
              const firstMessage = messages[groupIndices[0]]
              if (!firstMessage) return null

              const isOwnGroup = firstMessage.sender.id === authUser?.id

              return (
                <div key={`series-${firstMessage.id}`} className={getMessageSeriesGroupClassName(isOwnGroup)}>
                  {groupIndices.map(index => {
                    const message = messages[index]
                    if (!message) return null

                    const isOwnMessage = message.sender.id === authUser?.id
                    const isSystemMessage = message.isSystem
                    const isHighlighted = highlightedMessageId === message.id
                    const isLatestMessage = index === messages.length - 1

                    const previousMessage = index > 0 ? messages[index - 1] : null
                    const showTimestampDivider = shouldShowTimestampDivider(message, previousMessage, 30)
                    const seriesMeta = getMessageSeriesMeta(messages, index)

                    return (
                      <React.Fragment key={message.id}>
                        {showTimestampDivider && (
                          <TimestampDivider
                            timestamp={message.createdAt}
                            locale="vi"
                            formatFunction={formatTimestampDivider}
                          />
                        )}

                        <div
                          data-message-id={message.id}
                          className={`flex w-full origin-center ${isOwnMessage ? 'justify-end' : 'justify-start'} group ${
                            isHighlighted ? 'animate-message-pop' : ''
                          }`}
                        >
                    {isOwnMessage ? (
                      /* Own message layout */
                      <div className="flex flex-col items-end max-w-[280px] sm:max-w-xs lg:max-w-md">
                        {/* Message box with actions */}
                        <MessageBoxWithActions
                          message={message}
                          isOwnMessage={isOwnMessage}
                          isSystemMessage={isSystemMessage}
                          editingMessageId={editingMessageId}
                          editMessageContent={editMessageContent}
                          onStartEditMessage={onStartEditMessage}
                          onEditMessage={onEditMessage}
                          onCancelEditMessage={onCancelEditMessage}
                          onEditMessageContentChange={onEditMessageContentChange}
                          onShowDeleteConfirm={onShowDeleteConfirm}
                          onStartReply={onStartReply}
                          renderFileMessage={renderFileMessage}
                          formatTimestamp={formatTimestamp}
                          getMessageViewers={getMessageViewers}
                          setViewingMessage={setViewingMessage}
                          scrollManager={scrollManager}
                          onScrollToMessage={onScrollToMessage}
                          onMessageReact={onMessageReact}
                          currentUser={authUser}
                          onPinMessage={onPinMessage}
                          onUnpinMessage={onUnpinMessage}
                          conversationId={selectedConversationId}
                          isLatestMessage={isLatestMessage}
                          seriesPosition={seriesMeta.position}
                        />
                      </div>
                    ) : (
                      /* Other user message layout */
                      <div className="flex flex-col items-start max-w-[280px] sm:max-w-xs lg:max-w-md">
                        {seriesMeta.showSenderHeader && (
                          <MessageUserAvatar
                            sender={message.sender}
                            onSenderClick={onMessageSenderClick}
                          />
                        )}

                        {/* Message box with actions */}
                        <div className='w-full'>
                        <MessageBoxWithActions
                          message={message}
                          isOwnMessage={isOwnMessage}
                          isSystemMessage={isSystemMessage}
                          editingMessageId={editingMessageId}
                          editMessageContent={editMessageContent}
                          onStartEditMessage={onStartEditMessage}
                          onEditMessage={onEditMessage}
                          onCancelEditMessage={onCancelEditMessage}
                          onEditMessageContentChange={onEditMessageContentChange}
                          onShowDeleteConfirm={onShowDeleteConfirm}
                          onStartReply={onStartReply}
                          renderFileMessage={renderFileMessage}
                          formatTimestamp={formatTimestamp}
                          getMessageViewers={getMessageViewers}
                          setViewingMessage={setViewingMessage}
                          scrollManager={scrollManager}
                          onScrollToMessage={onScrollToMessage}
                          onMessageReact={onMessageReact}
                          currentUser={authUser}
                          onPinMessage={onPinMessage}
                          onUnpinMessage={onUnpinMessage}
                          conversationId={selectedConversationId}
                          seriesPosition={seriesMeta.position}
                        />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Show Load Newer Messages button after context messages */}
                  {contextMessagesEndIndex !== null &&
                    index === contextMessagesEndIndex &&
                    hasMoreMessages &&
                    !isLoadingMore && (
                      <div ref={loadMoreButtonCallbackRef} className="flex justify-center py-2">
                        <Button
                          onClick={onLoadMoreMessages}
                          disabled={isLoadingMore}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          Load More Messages
                        </Button>
                      </div>
                    )}

                        {/* Show loading indicator for newer messages */}
                        {contextMessagesEndIndex !== null && index === contextMessagesEndIndex && isLoadingMore && (
                          <div className="flex justify-center py-2">
                            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                              <div className="w-3 h-3 sm:w-4 sm:h-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              Loading more messages...
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              )
            })}
          </>
        )}

        {isTyping && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-gray-100 px-3 py-2 text-xs text-gray-600 border border-gray-200">
              {typingUserName ? `${typingUserName} is typing...` : 'Typing...'}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {isScrolledUp && (
        <div className="pointer-events-none absolute bottom-3 left-0 right-0 z-10 flex justify-center">
          <Button
            onClick={scrollManager.scrollToBottom}
            size="sm"
            variant="outline"
            className="pointer-events-auto h-10 w-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 backdrop-blur-sm"
            title="Scroll to bottom"
          >
            <ChevronDown className="h-4 w-4 text-gray-600" />
          </Button>
        </div>
      )}
      </div>

      {viewingMessage && (
        <MessageViewersDialog
          viewers={viewingMessage.viewers}
          onClose={() => setViewingMessage(null)}
          onUserClick={handleUserClick}
        />
      )}
    </>
  )
}

// Empty state component
export const ChatMessagesEmptyState: React.FC<{
  pendingTargetUserId?: number
  isCreatingConversation: boolean
  isLoading: boolean
}> = ({ pendingTargetUserId, isCreatingConversation, isLoading }) => (
  <div className="flex-1 flex items-center justify-center text-gray-500 p-4">
    <div className="text-center">
      <MessageCircle className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
      <p className="text-base sm:text-lg font-medium">
        {pendingTargetUserId && (isCreatingConversation || isLoading)
          ? isLoading
            ? 'Loading conversations...'
            : 'Creating conversation...'
          : pendingTargetUserId
            ? `Start chatting`
            : 'Welcome to General Chat'}
      </p>
      <p className="text-xs sm:text-sm mt-2">
        {pendingTargetUserId && (isCreatingConversation || isLoading)
          ? isLoading
            ? 'Please wait while we load your conversations'
            : 'Please wait while we set up your chat'
          : pendingTargetUserId
            ? 'Type a message below to create a new conversation and send your first message'
            : 'Select a conversation from the sidebar or start typing to create a new one'}
      </p>
      {pendingTargetUserId && (isCreatingConversation || isLoading) && (
        <div className="mt-4">
          <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      )}
    </div>
  </div>
)

export function ChatMessageFileContentItem({ fileData }: { fileData: FileMessage }) {
  const handleDownload = async () => {
    try {
      const response = await fetch(fileData.fileUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileData.fileName || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to download file:', error)
      // Fallback to opening in new tab if download fails
      window.open(fileData.fileUrl, '_blank')
    }
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 bg-gray-50 rounded-lg border max-w-full sm:max-w-xs">
        <div className="h-6 w-6 text-blue-600 flex-shrink-0 mb-2 sm:mb-0 sm:mr-2 hidden sm:block">📎</div>
        <div className="flex-1 min-w-0 w-full">
          <p className="text-sm font-medium text-gray-900 truncate">{fileData.fileName || 'Unknown file'}</p>
          {fileData.fileSize && (
            <p className="text-xs text-gray-500">{(fileData.fileSize / 1024 / 1024).toFixed(1)} MB</p>
          )}
        </div>
        <button
          onClick={handleDownload}
          className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 flex-shrink-0 sm:mt-0"
          aria-label="Download file"
        >
          ⬇️
        </button>
    </div>
  )
}

export function ChatMessageVideoContentItem({ fileData }: { fileData: FileMessage }) {
  const handleDownload = async () => {
    try {
      const response = await fetch(fileData.fileUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileData.fileName || 'video'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to download video:', error)
      window.open(fileData.fileUrl, '_blank')
    }
  }

  return (
    <div className="max-w-xs sm:max-w-md">
      <div className="relative overflow-hidden rounded-lg bg-black border border-gray-100">
        <video src={fileData.fileUrl} controls className="w-full h-auto max-h-72 bg-black" preload="metadata" />
        <button
          onClick={handleDownload}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
          aria-label="Download video"
        >
          ⬇️
        </button>
      </div>
    </div>
  )
}

export function ChatMessageImageContentItem({ fileData }: { fileData: FileMessage }) {
  const { isOpen, currentImage, openPreview, closePreview } = useImagePreview()

  return (
    <>
      <div className="max-w-[200px]">
        <Image
          src={fileData.fileUrl}
          alt={fileData.fileName || 'Shared image'}
          className="w-full h-auto max-h-40 rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => openPreview(fileData.fileUrl, fileData.fileName || 'Shared image')}
          width={200}
          height={160}
          unoptimized={true}
        />
      </div>

      {currentImage && (
        <ImagePreviewModal
          src={currentImage.src}
          alt={currentImage.alt}
          isOpen={isOpen}
          onClose={closePreview}
          showDownload={true}
        />
      )}
    </>
  )
}
